import { QueryClientProvider } from '@tanstack/react-query';
import { ChatApiVersionGuard, TooltipProvider } from '@odo/shared-ui';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { ToastContainer } from './components/Toast';
import { UpdateBanner } from './components/UpdateBanner';
import { SidebarLayout } from './layouts/SidebarLayout';
import { Forbidden } from './pages/Forbidden';
import { Account } from './pages/Account';
import { Home } from './pages/Home';
import { Patients } from './pages/Patients';
import { Login } from './pages/Login';
import Conversations from './pages/Conversations';
import { WorkQueue } from './pages/WorkQueue';
import { PatientFiles } from './pages/PatientFiles';
import { ResourcesRoutes } from './Routes/resources';
import { OverviewDashboard } from './pages/billing/OverviewDashboard';
import { ChargesList } from './pages/billing/ChargesList';
import { InvoicesList } from './pages/billing/InvoicesList';
import { OneTimeDiscountsList } from './pages/billing/OneTimeDiscountsList';
import { PurchaseOrdersList } from './pages/billing/PurchaseOrdersList';
import { VendorsList } from './pages/billing/VendorsList';
import { ProductsTable } from './pages/billing/ProductsTable';
import { DiscountsPage } from './pages/billing/DiscountsPage';
import { AccountsOverview } from './pages/accounts/AccountsOverview';
import { PracticesList } from './pages/accounts/PracticesList';
import { UsersList } from './pages/accounts/UsersList';
import { PracticeDetail } from './pages/accounts/PracticeDetail';
import { PracticeCreatePage } from './pages/accounts/PracticeCreatePage';
import { OrthodontistsList } from './pages/accounts/OrthodontistsList';
import { OrthodontistCreatePage } from './pages/accounts/OrthodontistCreatePage';
import { OrthodontistDetail } from './pages/accounts/OrthodontistDetail';
import { CommunicationFormPage } from './pages/accounts/CommunicationFormPage';
import { CommunicationDetailPage } from './pages/accounts/CommunicationDetailPage';
import { CommunicationsList } from './pages/accounts/CommunicationsList';
import { AccessUsersList } from './pages/access/AccessUsersList';
import { AccessTeams } from './pages/access/AccessTeams';
import { AccessRoles } from './pages/access/AccessRoles';
import { AccessPermissions } from './pages/access/AccessPermissions';
import { AccessRoutes } from './pages/access/AccessRoutes';
import { EventsList } from './pages/EventsList';
import { MonitoringOverview } from './pages/monitoring/MonitoringOverview';
import { MonitoringNotifications } from './pages/monitoring/MonitoringNotifications';
import { MonitoringNotificationView } from './pages/monitoring/MonitoringNotificationView';
import { MonitoringMessages } from './pages/monitoring/MonitoringMessages';
import { MonitoringMessageView } from './pages/monitoring/MonitoringMessageView';
import { EventDetailPage } from './pages/accounts/EventDetailPage';
import { PageHeaderProvider } from './providers/PageHeaderProvider';
import { NavigationLockProvider } from './providers/NavigationLockProvider';
import { queryClient } from './providers/queryClient';
import { ToastProvider } from './providers/ToastProvider';
import { useUser, UserProvider } from './providers/UserProvider';
import { useMyPermissions } from './hooks/useMyPermissions';
import { WhatsNew } from './components/whats-new/WhatsNew';
import { WhatsNewProvider } from './components/whats-new/WhatsNewProvider';
import type { KnownPermission } from './services/iam';

function invalidateAllQueries(): void {
    void queryClient.invalidateQueries();
}

// IAM-driven gates (GET /iam/me): spinner while loading, an error state (not a
// redirect) on fetch failure, redirect only once permissions are known and lacking.
function RequirePermission({
    permission,
    children
}: {
    permission: KnownPermission;
    children: React.ReactNode;
}) {
    const { isLoading, isError, hasPermission, refetch } = useMyPermissions();
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
            </div>
        );
    }
    if (isError) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-50">
                <p className="text-sm text-gray-600">Couldn't load your permissions.</p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                >
                    Retry
                </button>
            </div>
        );
    }
    if (!hasPermission(permission)) {
        return <Navigate to="/work-queue" replace />;
    }
    return <>{children}</>;
}

function BillingRoute({ children }: { children: React.ReactNode }) {
    return <RequirePermission permission="billing.get">{children}</RequirePermission>;
}

function AccountsRoute({ children }: { children: React.ReactNode }) {
    return <RequirePermission permission="practices.get">{children}</RequirePermission>;
}

// List/detail reads stay open to every files-viewer user - the PII sign-ups
// read is gated separately, in-page on `events.get` (it's a tab, not a route).
function EventsRoute({ children }: { children: React.ReactNode }) {
    return <>{children}</>;
}

// Legacy Calls-feature URLs need a param `<Navigate>` alone can't rewrite - these
// resolve it at render time (the migration kept ids, so an old call id IS the new thread id).
function LegacyPracticeCommunicationsNewRedirect() {
    const { practiceId } = useParams<{ practiceId: string }>();
    return <Navigate to={`/accounts/practices/${practiceId}/communications/new`} replace />;
}

function LegacyPracticeCommunicationRedirect() {
    const { practiceId, callId } = useParams<{ practiceId: string; callId: string }>();
    return <Navigate to={`/accounts/practices/${practiceId}/communications/${callId}`} replace />;
}

function AccessRoute({ children }: { children: React.ReactNode }) {
    return <RequirePermission permission="iam.get">{children}</RequirePermission>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { user, isLoading } = useUser();
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    return (
        <>
            <ChatApiVersionGuard onSwitch={invalidateAllQueries} />
            <WhatsNew />
            {children}
        </>
    );
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forbidden" element={<Forbidden />} />
            <Route
                path="/home"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <Home />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/patients"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <Patients />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/work-queue"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <WorkQueue />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/conversations"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <Conversations />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/patients/:id"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <PatientFiles />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/account"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <Account />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/resources/*"
                element={
                    <ProtectedRoute>
                        <ResourcesRoutes />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/monitoring/overview"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <MonitoringOverview />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/monitoring/notifications"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <MonitoringNotifications />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/monitoring/notifications/:patientId"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <MonitoringNotificationView />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/monitoring/messages"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <MonitoringMessages />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/monitoring/messages/:patientId"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <MonitoringMessageView />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route path="/monitoring" element={<Navigate to="/monitoring/overview" replace />} />
            <Route
                path="/billing/overview"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <OverviewDashboard />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/charges"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <ChargesList />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/charges/:chargeId"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <ChargesList />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/invoices"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <InvoicesList />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/invoices/:invoiceId"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <InvoicesList />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/purchase-orders"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <PurchaseOrdersList />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/purchase-orders/:poId"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <PurchaseOrdersList />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/one-time-discounts"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <OneTimeDiscountsList />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/vendors"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <VendorsList />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/vendors/:vendorId"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <VendorsList />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/products"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <ProductsTable />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/billing/discounts"
                element={
                    <ProtectedRoute>
                        <BillingRoute>
                            <SidebarLayout>
                                <DiscountsPage />
                            </SidebarLayout>
                        </BillingRoute>
                    </ProtectedRoute>
                }
            />
            <Route path="/billing" element={<Navigate to="/billing/overview" replace />} />
            <Route
                path="/accounts/overview"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout>
                                <AccountsOverview />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/practices"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout>
                                <PracticesList />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/practices/new"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout hideHeader>
                                <PracticeCreatePage />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/practices/:practiceId"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout hideHeader>
                                <PracticeDetail />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/orthodontists"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout>
                                <OrthodontistsList />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/orthodontists/new"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout hideHeader>
                                <OrthodontistCreatePage />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/orthodontists/:orthodontistId"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout hideHeader>
                                <OrthodontistDetail />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/practices/:practiceId/communications/new"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout hideHeader>
                                <CommunicationFormPage />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/practices/:practiceId/communications/:communicationId"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout hideHeader>
                                <CommunicationDetailPage />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/communications/new"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout hideHeader>
                                <CommunicationFormPage />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/communications/:communicationId"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout hideHeader>
                                <CommunicationDetailPage />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/communications"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout>
                                <CommunicationsList />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            {/* Legacy Calls-feature redirects - the migration preserves ids,
                so an old call id IS the new thread id. */}
            <Route
                path="/accounts/calls"
                element={<Navigate to="/accounts/communications" replace />}
            />
            <Route
                path="/accounts/calls/new"
                element={<Navigate to="/accounts/communications/new" replace />}
            />
            <Route
                path="/accounts/practices/:practiceId/calls/new"
                element={<LegacyPracticeCommunicationsNewRedirect />}
            />
            <Route
                path="/accounts/practices/:practiceId/calls/:callId"
                element={<LegacyPracticeCommunicationRedirect />}
            />
            <Route
                path="/accounts/users"
                element={
                    <ProtectedRoute>
                        <AccountsRoute>
                            <SidebarLayout>
                                <UsersList />
                            </SidebarLayout>
                        </AccountsRoute>
                    </ProtectedRoute>
                }
            />
            <Route path="/accounts" element={<Navigate to="/accounts/overview" replace />} />
            {/* The page moved under Accounts; the old URL is in people's tabs
                and bookmarks, so it redirects rather than 404ing. */}
            <Route path="/events" element={<Navigate to="/accounts/events" replace />} />
            <Route
                path="/access/users"
                element={
                    <ProtectedRoute>
                        <AccessRoute>
                            <SidebarLayout>
                                <AccessUsersList />
                            </SidebarLayout>
                        </AccessRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/access/users/:userId"
                element={
                    <ProtectedRoute>
                        <AccessRoute>
                            <SidebarLayout>
                                <AccessUsersList />
                            </SidebarLayout>
                        </AccessRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/access/teams"
                element={
                    <ProtectedRoute>
                        <AccessRoute>
                            <SidebarLayout>
                                <AccessTeams />
                            </SidebarLayout>
                        </AccessRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/access/teams/:teamId"
                element={
                    <ProtectedRoute>
                        <AccessRoute>
                            <SidebarLayout>
                                <AccessTeams />
                            </SidebarLayout>
                        </AccessRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/access/roles"
                element={
                    <ProtectedRoute>
                        <AccessRoute>
                            <SidebarLayout>
                                <AccessRoles />
                            </SidebarLayout>
                        </AccessRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/access/permissions"
                element={
                    <ProtectedRoute>
                        <AccessRoute>
                            <SidebarLayout>
                                <AccessPermissions />
                            </SidebarLayout>
                        </AccessRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/access/routes"
                element={
                    <ProtectedRoute>
                        <AccessRoute>
                            <SidebarLayout>
                                <AccessRoutes />
                            </SidebarLayout>
                        </AccessRoute>
                    </ProtectedRoute>
                }
            />
            <Route path="/access" element={<Navigate to="/access/users" replace />} />
            <Route
                path="/accounts/events"
                element={
                    <ProtectedRoute>
                        <EventsRoute>
                            <SidebarLayout>
                                <EventsList />
                            </SidebarLayout>
                        </EventsRoute>
                    </ProtectedRoute>
                }
            />
            {/* `new` before `:eventId`, so the literal wins over the param. */}
            <Route
                path="/accounts/events/new"
                element={
                    <ProtectedRoute>
                        <EventsRoute>
                            <SidebarLayout hideHeader>
                                <EventDetailPage />
                            </SidebarLayout>
                        </EventsRoute>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounts/events/:eventId"
                element={
                    <ProtectedRoute>
                        <EventsRoute>
                            <SidebarLayout hideHeader>
                                <EventDetailPage />
                            </SidebarLayout>
                        </EventsRoute>
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <TooltipProvider>
                <UserProvider>
                    <ToastProvider>
                        <WhatsNewProvider>
                            <BrowserRouter>
                                <NavigationLockProvider>
                                    <PageHeaderProvider>
                                        <AppRoutes />
                                    </PageHeaderProvider>
                                    <ToastContainer />
                                    <UpdateBanner />
                                </NavigationLockProvider>
                            </BrowserRouter>
                        </WhatsNewProvider>
                    </ToastProvider>
                </UserProvider>
            </TooltipProvider>
        </QueryClientProvider>
    );
}

export default App;
